import * as React from "react";
import { Button as MuiButton } from '@mui/material';
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

// Create buttonVariants function separately for HMR stability
const createButtonVariants = () => cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-semibold tracking-[0.01em] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 active:scale-[0.985]",
  {
    variants: {
      variant: {
        default: "bg-gradient-to-br from-blue-500 via-blue-600 to-cyan-600 text-white shadow-[0_14px_28px_-14px_rgba(29,78,216,0.9)] hover:from-blue-500 hover:via-blue-600 hover:to-cyan-500 hover:shadow-[0_18px_34px_-14px_rgba(29,78,216,0.92)]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-blue-200/80 bg-white/92 text-blue-700 shadow-sm hover:border-blue-300 hover:bg-blue-50/85 hover:text-blue-800 dark:border-blue-400/30 dark:bg-slate-900/60 dark:text-blue-200 dark:hover:bg-blue-500/10",
        secondary: "bg-sky-50 text-blue-700 border border-sky-100 shadow-sm hover:bg-sky-100/80 dark:bg-blue-500/15 dark:text-blue-200 dark:border-blue-400/20 dark:hover:bg-blue-500/20",
        ghost: "text-blue-700 hover:bg-blue-50/80 hover:text-blue-800 dark:text-blue-200 dark:hover:bg-blue-500/10",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-lg px-3",
        lg: "h-11 rounded-xl px-8",
        icon: "h-10 w-10 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export const buttonVariants = createButtonVariants();

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function ButtonComponent({ className, variant, size, asChild = false, children, ...props }: ButtonProps, ref: React.Ref<HTMLButtonElement>) {
  const Comp = asChild ? Slot : "button";
  
  // Use MUI Button for consistent styling
  if (!asChild) {
    const muiVariant = variant === 'outline' ? 'outlined' : 
                      variant === 'ghost' || variant === 'link' ? 'text' : 
                      'contained';
    
    const muiSize = size === 'sm' ? 'small' as const :
                   size === 'lg' ? 'large' as const : 
                   'medium' as const;

    // Filter out problematic props
    const { color, ...filteredProps } = props;

    return (
      <MuiButton
        ref={ref as any}
        variant={muiVariant}
        size={muiSize}
        className={cn(buttonVariants({ variant, size, className }))}
        {...filteredProps}
      >
        {children}
      </MuiButton>
    );
  }

  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {children}
    </Comp>
  );
}

export const Button = React.forwardRef(ButtonComponent);
Button.displayName = "Button";